import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  ShoppingCart, 
  TrendingUp, 
  Package,
  Search,
  BarChart3,
  Target,
  Home,
  Star,
  Truck
} from 'lucide-react';

const AmazonOptimierung = () => {
  return (
    <div className="page-container">
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Home className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Amazon Optimierung</span>
          </div>
        </div>
      </div>

      <section className="hero-section-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Banner Image */}
          <div className="mb-8 rounded-xl overflow-hidden" style={{maxHeight: '200px'}}>
            <img 
              src="https://images.pexels.com/photos/3867357/pexels-photo-3867357.jpeg" 
              alt="Amazon E-Commerce" 
              className="w-full h-48 object-cover"
            />
          </div>
          
          {/* Content Grid */}
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h1 className="service-page-title">
                Amazon Optimierung – SEO, VCS Lite, FBA & Vendor Central
              </h1>
              <p className="service-page-subtitle">
                Ich optimiere Ihre Amazon-Präsenz: von Produkt-SEO über VCS Lite bis FBA-Logistik und Vendor Central. Mehr Sichtbarkeit, weniger Fehler, höhere Margen durch datengetriebene Optimierung.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/#kontakt">
                  <Button size="lg" className="cta-button-large">
                    Kostenlose Erstanalyse
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>+10-30% Marge</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Besseres Ranking</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Weniger Fehler</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">Amazon-Leistungen</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Search className="h-6 w-6" />
                </div>
                <h3 className="service-title">Amazon SEO</h3>
                <p className="service-description">
                  Keyword-Recherche, Titel-Optimierung, Bullet Points, Backend-Keywords und A+ Content für besseres Ranking.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Package className="h-6 w-6" />
                </div>
                <h3 className="service-title">VCS Lite Optimierung</h3>
                <p className="service-description">
                  Vendor Central Management, Bestellvorhersage, Retouren-Optimierung und automatisierte Prozesse.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Truck className="h-6 w-6" />
                </div>
                <h3 className="service-title">FBA-Logistik</h3>
                <p className="service-description">
                  Lagerbestandsoptimierung, Versandplan-Automatisierung, Kostenkontrolle und Retouren-Management.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <BarChart3 className="h-6 w-6" />
                </div>
                <h3 className="service-title">Performance-Analyse</h3>
                <p className="service-description">
                  Sales-Tracking, Conversion-Optimierung, Werbekosten-Analyse (ACoS) und Profitabilitäts-Monitoring.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <h3 className="service-title">Preisoptimierung</h3>
                <p className="service-description">
                  Dynamische Preisanpassung, Wettbewerber-Monitoring, Buy Box Optimierung und Margen-Kalkulation.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Star className="h-6 w-6" />
                </div>
                <h3 className="service-title">Review-Management</h3>
                <p className="service-description">
                  Bewertungs-Monitoring, Kunden-Feedback-Analyse und Optimierung der Produktqualität.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="visual-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title-dark">Typische Margenverbesserung</h2>
          <div className="text-6xl font-bold text-accent my-8">+10-30%</div>
          <p className="text-xl text-gray-300">
            Durch optimierte Preisformeln, besseres Ranking und weniger Fehler steigern Händler ihre Marge um 10-30%.
          </p>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Bereit für mehr Amazon-Erfolg?</h2>
          <p className="section-subtitle mb-8">
            Lassen Sie uns Ihr Amazon-Geschäft auf das nächste Level bringen.
          </p>
          <Link to="/#kontakt">
            <Button size="lg" className="cta-button-large">
              Jetzt kostenlose Erstanalyse anfragen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AmazonOptimierung;